import { type ReactNode, useEffect, useState } from 'react';
import { Spinner } from '@utilities-one/gpm-ui';

import { getBrowserName, isGoogleChromeBrowser } from '@/components/browser/isGoogleChromeBrowser';

const getChromeOnlyMessage = () =>
  `REIPM supports only Google Chrome. Your current browser is ${getBrowserName()}. Please switch to Chrome and try again.`;

type GateState = 'checking' | 'allowed' | 'blocked';

/** Gate around the signed-in app: renders children only in Google Chrome; other browsers get an alert + blocking message. */
export const ChromeOnlyGate = ({ children }: Readonly<{ children: ReactNode }>) => {
  const [gateState, setGateState] = useState<GateState>('checking');

  useEffect(() => {
    if (!isGoogleChromeBrowser()) {
      window.alert(getChromeOnlyMessage());
      setGateState('blocked');
      return;
    }

    setGateState('allowed');
  }, []);

  if (gateState === 'checking') {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center" role="status" aria-label="Loading">
        <Spinner className="size-8" />
      </div>
    );
  }

  if (gateState === 'blocked') {
    return (
      <div className="bg-background flex min-h-screen flex-col items-center justify-center p-6 text-center">
        <p className="text-foreground max-w-md text-lg">{getChromeOnlyMessage()}</p>
      </div>
    );
  }

  return <>{children}</>;
};
